
import React, { useState, useRef, useEffect } from 'react';
import { Mic, Square, Play, Send, Trash2, Loader, CheckCircle } from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../apiConfig';

const VoiceRecorder = ({ customerId, leadId, visitId, onUploadSuccess }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);

    const mediaRecorderRef = useRef(null);
    const timerRef = useRef(null);
    const audioRef = useRef(null);
    // Remember the exact format we recorded in, so the upload label always
    // matches the bytes — no browser-specific guessing at send time.
    const recordedMimeRef = useRef(null);

    // Map a recorder MIME type to a file extension the backend + Gemini accept.
    // The container the browser actually produces (webm on Chrome/FF, mp4 on
    // Safari/iOS) MUST match the extension/MIME we send, or Gemini fails to decode.
    const mimeToExt = {
        'audio/webm': 'webm',
        'audio/ogg': 'ogg',
        'audio/mp4': 'mp4',
        'audio/aac': 'aac',
        'audio/mpeg': 'mp3',
        'audio/wav': 'wav',
    };

    // Pick the first capture format THIS browser supports. Order is deliberate:
    // Chrome/Firefox land on webm; Safari/iOS (no webm support) land on mp4.
    // Returns null if the browser can't introspect — caller falls back safely.
    const pickSupportedMime = () => {
        const candidates = ['audio/webm', 'audio/mp4', 'audio/ogg', 'audio/aac'];
        if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
            return null;
        }
        return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || null;
    };

    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (audioUrl) URL.revokeObjectURL(audioUrl);
        };
    }, [audioUrl]);

    const startRecording = async () => {
        setError(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            // Record in a format this browser actually supports, then label the
            // Blob/upload with that SAME format. Hardcoding mp4 broke Chrome/FF
            // (which produce webm); biasing to webm would break Safari/iOS (mp4).
            const supportedType = pickSupportedMime();
            mediaRecorderRef.current = supportedType
                ? new MediaRecorder(stream, { mimeType: supportedType })
                : new MediaRecorder(stream);

            const chunks = [];
            mediaRecorderRef.current.ondataavailable = (e) => {
                if (e.data.size > 0) chunks.push(e.data);
            };

            mediaRecorderRef.current.onstop = () => {
                // Use the recorder's real MIME type (strip any ";codecs=..." suffix).
                // Safari leaves mimeType populated even when we don't pass one in.
                const recordedType = (mediaRecorderRef.current?.mimeType || supportedType || 'audio/mp4').split(';')[0];
                recordedMimeRef.current = recordedType;
                const blob = new Blob(chunks, { type: recordedType });
                const url = URL.createObjectURL(blob);
                setAudioBlob(blob);
                setAudioUrl(url);
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
            setRecordingTime(0);

            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);

        } catch (err) {
            console.error("Error accessing microphone:", err);
            setError("Microphone blocked. Allow access in your browser settings.");
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            clearInterval(timerRef.current);
        }
    };

    const discardRecording = () => {
        setAudioBlob(null);
        setAudioUrl(null);
        setRecordingTime(0);
        setUploadSuccess(false);
    };

    const uploadRecording = async () => {
        setError(null);
        if (!audioBlob) return;

        setIsUploading(true);
        const formData = new FormData();
        // Append ID based on what we have (customer or lead)
        if (customerId) formData.append('customer_id', customerId);
        if (leadId) formData.append('lead_id', leadId);
        // Link to the originating visit so the voice summary/sentiment consolidate
        // into the same timeline card as the visit's check-in/completion.
        if (visitId) formData.append('visit_id', visitId);

        // Derive the filename extension from the format we actually recorded in
        // (webm on Chrome/FF, mp4 on Safari/iOS) so the backend and Gemini get a
        // correctly-labeled file. Fall back to the recorded mime, never a guess.
        const blobType = (audioBlob.type || recordedMimeRef.current || 'audio/mp4').split(';')[0];
        const ext = mimeToExt[blobType] || 'mp4';
        formData.append('audio', audioBlob, `feedback.${ext}`);

        try {
            // Do NOT set Content-Type manually — when posting FormData, axios (and the
            // browser) must auto-generate "multipart/form-data; boundary=----..." with
            // the correct boundary string. An explicit Content-Type header without the
            // boundary causes FastAPI to return 422 (unprocessable entity).
            const response = await axios.post(`${API_BASE_URL}/api/agents/feedback/voice`, formData, {
                withCredentials: true
            });

            if (response.data.status === 'success') {
                setUploadSuccess(true);
                // Pass full response data including extraction to parent
                if (onUploadSuccess) {
                    onUploadSuccess({
                        ...response.data,
                        customerId,
                        leadId
                    });
                }
                setTimeout(() => {
                    discardRecording(); // Reset after 2s success msg
                }, 2000);
            }
        } catch (err) {
            console.error("Upload failed", err);
            // Surface the backend's actual reason (e.g. rate limit, AI failure)
            // instead of a generic message so failures are diagnosable.
            const detail = err?.response?.data?.detail;
            setError(detail ? `Upload failed: ${detail}` : "Upload failed. Please try again.");
        } finally {
            setIsUploading(false);
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Render states
    if (uploadSuccess) {
        return (
            <div className="flex items-center gap-2 text-emerald-600 font-medium text-sm animate-pulse">
                <CheckCircle className="w-5 h-5" />
                <span>Sent!</span>
            </div>
        );
    }

    // Failures used to be browser alerts. This is a small control, so the
    // message sits beside it: a blocked microphone lands on the mic button, a
    // failed upload lands on the recording the user still has in hand. Declared
    // above the early returns below, which would otherwise read it before it
    // exists.
    const errorNote = error ? (
        <span role="alert" className="text-xs text-red-400 leading-snug max-w-[220px]">{error}</span>
    ) : null;

    if (isUploading) {
        return (
            <div className="flex items-center gap-2 text-blue-600 font-medium text-sm">
                <Loader className="w-4 h-4 animate-spin" />
                <span>Processing AI...</span>
            </div>
        );
    }

    if (audioBlob) {
        return (
            <span className="inline-flex items-center gap-2">
            <div className="flex items-center gap-2 bg-gray-700 rounded-full p-1 pl-3 pr-1">
                <button aria-label="Play recording" onClick={() => audioRef.current.play()} className="text-gray-300 hover:text-blue-600">
                    <Play className="w-4 h-4" />
                </button>
                <span className="text-xs font-mono text-gray-400">{formatTime(recordingTime)}</span>
                <audio ref={audioRef} src={audioUrl} className="hidden" />

                <div className="h-4 w-[1px] bg-gray-600 mx-1"></div>

                <button aria-label="Discard" onClick={discardRecording} className="p-1.5 text-red-500 hover:bg-red-900/40 rounded-full transition-colors" title="Discard">
                    <Trash2 className="w-4 h-4" />
                </button>
                <button aria-label="Send Feedback" onClick={uploadRecording} className="p-1.5 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors shadow-sm" title="Send Feedback">
                    <Send className="w-4 h-4" />
                </button>
            </div>
            {errorNote}
            </span>
        );
    }

    if (isRecording) {
        return (
            <div className="flex items-center gap-3 bg-red-900/40 border border-red-700 rounded-full p-1 pl-3 pr-1 shadow-sm animate-pulse">
                <span className="text-xs font-mono text-red-600 font-bold">{formatTime(recordingTime)}</span>
                <button aria-label="Stop recording"
                    onClick={stopRecording}
                    className="p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                >
                    <Square className="w-4 h-4 fill-current" />
                </button>
            </div>
        );
    }

    // Default state: Mic Button
    return (
        <span className="inline-flex items-center gap-2">
        <button aria-label="Record Voice Note"
            onClick={startRecording}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-900/40 rounded-full transition-all"
            title="Record Voice Note"
        >
            <Mic className="w-5 h-5" />
        </button>
        {errorNote}
        </span>
    );
};

export default VoiceRecorder;
